import * as React from 'react'

import { Box, ColumnSplit } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import PanesHeader from 'modules/core/components/PanesHeader'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { usePerformanceModuleContext } from 'modules/performance/hooks/usePerfomanceModuleContext'
import ResponseValues from 'modules/reviews/components/WriteResponse/ResponseValues'
import { useQuestionGroup } from 'modules/reviews/hooks/useQuestionGroup'
import { useQuestionGroupCompetencies } from 'modules/reviews/hooks/useQuestionGroupsCompetencies'
import { useScore } from 'modules/reviews/hooks/useScore'
import { ScoreContext } from 'modules/reviews/lib/PerformanceScoreContext'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'
import type Review from 'modules/reviews/store/models/v2/Review'

import AgreementOnEvaluationSection from '../Agreements'
import ReviewUserTargetBlock from '../ReviewUserTargetBlock'
import BlankSlate from './BlankSlate'

interface QuestionsProps {
  questionGroup: QuestionGroup
  assessCompetencies: boolean
}

const Questions = ({ questionGroup, assessCompetencies }: QuestionsProps) => {
  if (!questionGroup.attributes.questions.length) {
    return <BlankSlate />
  }

  return <ResponseValues questionGroup={questionGroup} assessCompetencies={assessCompetencies} />
}

interface Props {
  questionGroupId: number
  form: Review
}

export default function Response({ form, questionGroupId }: Props) {
  const { performanceFeatureAvailability } = usePerformanceModuleContext()
  const { loading, questionGroup } = useQuestionGroup(questionGroupId)
  const { loading: loadingCompetencies } = useQuestionGroupCompetencies(
    performanceFeatureAvailability.canSeeCompetencies
  )
  const respondent = questionGroup && questionGroup.respondent
  const respondentJobTitle = respondent?.jobTitle
  const targetUser = questionGroup && questionGroup.target
  const targetUserjobTitle = targetUser && targetUser.jobTitle
  const isSelfReview = questionGroup?.respondentGroup?.attributes.name === 'self'
  const isManagerReview = questionGroup?.respondentGroup?.attributes.name === 'manager'

  const { buildInlineInfo, modifyScoreInlineInfo } = useScore()
  const { score } = React.useContext(ScoreContext)
  const inlineInfo = buildInlineInfo(score)
  modifyScoreInlineInfo(inlineInfo, score)

  if (loading || loadingCompetencies) return <Loading />

  if (!questionGroup) {
    return <NotFound />
  }

  const isCurrentCycle = questionGroup.attributes.cycle_id === form.currentCycleId

  const showAgreementsSection =
    hasFeature('performance_review_agreements') &&
    questionGroup.isAnswered &&
    isManagerReview &&
    (form.isFinished || !isCurrentCycle)

  return (
    <Box flexDirection="column" width="full">
      <Box paddingX="s48">
        {showAgreementsSection && <AgreementOnEvaluationSection questionGroup={questionGroup} />}
      </Box>
      <Box flexDirection="column" flexGrow>
        <ColumnSplit colored={false} minWidth="none">
          <PanesHeader
            illustration="surveys"
            title={form ? form.attributes.name : ''}
            description={i18n.t('surveys.form.response.description')}
            inlineInfo={inlineInfo}
          />
          <Box>
            {questionGroup && (
              <ReviewUserTargetBlock
                respondent={form.attributes.anonymous || !respondent ? null : respondent}
                description={respondentJobTitle}
                target={isSelfReview ? null : targetUser}
                targetDescription={targetUserjobTitle}
              >
                <Questions
                  questionGroup={questionGroup}
                  assessCompetencies={form.assessCompetencies}
                />
              </ReviewUserTargetBlock>
            )}
          </Box>
        </ColumnSplit>
      </Box>
    </Box>
  )
}
